import {lazy, FC, Suspense, useMemo} from 'react'
import {Route, Routes, Navigate, useLocation} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {ProtectedRoute} from '../components/common/ProtectedRoute'
import {TenantFeatureRoute} from '../tenant/TenantFeatureRoute'
import {AccessDenied} from '../pages/AccessDenied'
import {useAuth} from '../modules/auth'
import PuchaceReturnOrders from '../pages/orders/Orders'
import PurchaceOrderPage from '../pages/orders/PurchaceOrder'
import MyProfilePage from '../pages/my-profile/MyProfilePage'
import ContractInfoPage from '../pages/my-profile/ContractInfoPage'
import AddProductPage from '../pages/products/AddProductPage'
import AddProductBulkPage from '../pages/products/AddProductBulkPage'
import ProductsListPage from '../pages/products/ProductsListPage'
import ThankyouPage from '../pages/thank-you/ThankyouPage'
import SuggestedCostReqPage from '../pages/requests/SuggestedCostReqPage'
import AddProductReqPage from '../pages/requests/AddProductReqPage'
import UpdateProductBulkPage from '../pages/products/UpdateProductBulkPage'
import DuplicateBarcodesPage from '../pages/products/DuplicateBarcodesPage'
import BulkJobsList from '../modules/products/bulk-jobs/BulkJobsList'
import BulkJobReview from '../modules/products/bulk-jobs/BulkJobReview'
import UpdateProductReqPage from '../pages/requests/UpdateProductReqPage'
import AddSpecialBulkPage from '../pages/products/special/AddSpecialBulkPage'
import RemoveDiscountBulkPage from '../pages/products/special/RemoveDiscountBulkPage'
import SpecialSelectionPage from '../pages/products/special/SpecialSelectionPage'
import PendingSpecialPage from '../pages/products/special/PendingSpecialPage'
import AddDiscountReq from '../modules/requests/add-discount/AddDiscountReq'
import ConnectedBarcodesPage from '../pages/products/ConnectedBarcodesPage'
import EditProductPage from '../pages/products/EditProductPage'
import EditRequestPage from '../pages/requests/EditRequestPage'
import MyDashboardPage from '../pages/dashboard/MyDashboardPage'
import ReturnOrderPage from '../pages/orders/ReturnOrderPage'
import GRNInvoicesPage from '../pages/orders/GRNInvoicesPage'
import { POInvoicePageFromRoute } from '../pages/orders/POInvoicePage'
import SalesReportsPage from '../pages/reports/SalesReportsPage'
import TransactionReportsPage from '../pages/reports/TransactionReportsPage'
// Update import for the renamed page component
import PaymentReportsPage from '../pages/reports/PaymentReportsPage'
import StockReportPage from '../pages/reports/StockReportPage' // Import the new page

import DailyDealListPage from "../pages/daily-deals/DailyDealListPage";
import AppliedDailyDealListPage from "../pages/daily-deals/AppliedDailyDealListPage";
import LossOfOpportunityPage from "../pages/loss-of-opportunity/LossOfOpportunityPage";
import {InTransitReports} from "../modules/reports/InTransitReports";
import PickupFeesPage from '../pages/orders/PickupFeesPage'
import DraftProductsListPage from "../pages/products/DraftProductsListPage";
import AddBrandPage from "../pages/products/brand/AddBrandPage";
import OrderedProductsPage from "../pages/orders/OrderedProductsPage";
import AddToStockPage from '../pages/orders/AddToStockPage'
import SupplierDeliveryRecordPage from '../pages/orders/SupplierDeliveryRecordPage'
import DownloadAppPage from '../pages/DownloadAppPage'
import FeedbackPage from '../pages/FeedbackPage'
import {SupplierUsersPage} from '../modules/supplier-users/SupplierUsersPage'
import {PermissionOverviewPage} from '../pages/PermissionOverviewPage'
import RemovedProductsPage from '../pages/orders/RemovedProductsPage'
import OffersListPage from '../pages/offers/OffersListPage'
import FromBarcodesPage from '../pages/offers/FromBarcodesPage'

const PrivateRoutes = () => {
  const {currentUser} = useAuth()
  const location = useLocation()
  
  // Check if user has any module access
  const hasAnyAccess = useMemo(() => {
    if (currentUser?.role === 'admin') return true
    const permissions = currentUser?.permissions || {}
    return Object.values(permissions).some(p => p !== 'NONE')
  }, [currentUser])

  // Redirect to permission overview if no access, except on the three routes that must stay
  // reachable: /access-denied and /permission-overview (bouncing them would loop), and /myprofile.
  //
  // The /myprofile exemption only decides WHICH refusal a locked-out user sees. It does not make
  // the page reachable for them: /myprofile is wrapped in
  // <ProtectedRoute module='profile' requiredLevel='EDIT'> below, and a user with no access has
  // profile === 'NONE' by definition, so they get AccessDenied rather than a silent bounce. That
  // is deliberate — it is the more informative of the two — but do not read this exemption as
  // "a locked-out user can see their profile". They cannot, and they should not: "My Profile" is
  // the SUPPLIER COMPANY profile (bank details, contract, delivery zones), not their own account.
  if (!hasAnyAccess && 
      location.pathname !== '/myprofile' && 
      location.pathname !== '/feedback' &&
      location.pathname !== '/access-denied' &&
      location.pathname !== '/permission-overview') {
    return <Navigate to='/permission-overview' replace />
  }
  
  return (
    <Routes>
      <Route path="thank-you" element={<ThankyouPage />} />
      <Route path="access-denied" element={<AccessDenied />} />
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registration, or Permission Overview if no dashboard access */}
        <Route path='auth/*' element={
          <Navigate to={currentUser?.permissions?.dashboard !== 'NONE' ? '/dashboard' : '/permission-overview'} />
        } />
        {/* Pages */}
        {/* <Route path='dashboard' element={<DashboardWrapper />} /> */}
        <Route path='dashboard' element={
          <ProtectedRoute module='dashboard' requiredLevel='EDIT'>
            <MyDashboardPage />
          </ProtectedRoute>
        } />
        
        {/* Permission Overview - accessible to all authenticated users */}
        <Route path='permission-overview' element={<PermissionOverviewPage />} />
        {/* po order */}
        <Route path='orders' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <PuchaceReturnOrders />
          </ProtectedRoute>
        } />
        <Route path='orders/purchase-order/:orderID/invoice' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <POInvoicePageFromRoute />
          </ProtectedRoute>
        } />
        <Route path='orders/purchase-order/:orderID' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <PurchaceOrderPage />
          </ProtectedRoute>
        } />
        <Route path='orders/return-order/:orderID/invoice' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <POInvoicePageFromRoute />
          </ProtectedRoute>
        } />
        <Route path='orders/return-order/:orderID' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <ReturnOrderPage />
          </ProtectedRoute>
        } />
        <Route path='ordered-products' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <OrderedProductsPage />
          </ProtectedRoute>
        } />
        <Route path='orders/removed-products' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <RemovedProductsPage />
          </ProtectedRoute>
        } />
        <Route path='orders/supplier-delivery-record' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <SupplierDeliveryRecordPage />
          </ProtectedRoute>
        } />
        <Route path='orders/pickups_po_return_fees' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <PickupFeesPage />
          </ProtectedRoute>
        } />
        <Route path='orders/add-to-stock' element={
          <ProtectedRoute module='orders' requiredLevel='EDIT'>
            <AddToStockPage />
          </ProtectedRoute>
        } />
        {/* reports */}
        <Route path='sales/loss-of-opportunity' element={
          <ProtectedRoute module='loss_of_opportunity' requiredLevel='EDIT'>
            <LossOfOpportunityPage />
          </ProtectedRoute>
        } />
        <Route path='sales/sales-report' element={
          <ProtectedRoute module='sales_reports' requiredLevel='EDIT'>
            <SalesReportsPage />
          </ProtectedRoute>
        } />
        <Route path='sales/in-transit' element={
          <ProtectedRoute module='sales_reports' requiredLevel='EDIT'>
            <InTransitReports />
          </ProtectedRoute>
        } />
        <Route path='payment/transaction-report' element={
          <ProtectedRoute module='payment_reports' requiredLevel='EDIT'>
            <TransactionReportsPage />
          </ProtectedRoute>
        } />
        {/* Update route path and element */}
        <Route path='payment/payment-report' element={
          <ProtectedRoute module='payment_reports' requiredLevel='EDIT'>
            <PaymentReportsPage />
          </ProtectedRoute>
        } />
        <Route path='stock-report' element={
          <ProtectedRoute module='stock_reports' requiredLevel='EDIT'>
            <StockReportPage />
          </ProtectedRoute>
        } /> {/* Add the new route */}
        {/* supplier profile */}
        <Route path='myprofile' element={
          <ProtectedRoute module='profile' requiredLevel='EDIT'>
            <MyProfilePage />
          </ProtectedRoute>
        } />
        <Route path='contract-info' element={
          <ProtectedRoute module='profile' requiredLevel='EDIT'>
            <ContractInfoPage />
          </ProtectedRoute>
        } />

        {/* products */}
        <Route path='products/list' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <ProductsListPage />
          </ProtectedRoute>
        } />
        {/* multi-supplier: My Offers — same permission gate as products
            (suppliers who can edit products can edit their own offers). */}
        <Route path='offers/list' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <OffersListPage />
          </ProtectedRoute>
        } />
        {/* multi-supplier B1: bulk "Sell Yours" — create/update offers for
            many barcodes on one page (duplicate-barcode style). Reached from
            Bulk Add's used-barcode rows (B2) and as a standalone entry. */}
        <Route path='offers/from-barcodes' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <FromBarcodesPage />
          </ProtectedRoute>
        } />
        <Route path='products/custom/list' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <ProductsListPage />
          </ProtectedRoute>
        } />
        <Route path='grn-invoices/:orderID' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <GRNInvoicesPage />
          </ProtectedRoute>
        } />
        <Route path='products/product-add' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <AddProductPage />
          </ProtectedRoute>
        } />
        <Route path='products/product-edit/:productID' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <EditProductPage />
          </ProtectedRoute>
        } />
        <Route path='products/bulk-add' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <AddProductBulkPage />
          </ProtectedRoute>
        } />
        <Route path='products/bulk-update' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <UpdateProductBulkPage />
          </ProtectedRoute>
        } />
        <Route path='products/duplicate-barcodes' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <DuplicateBarcodesPage />
          </ProtectedRoute>
        } />
        <Route path='products/bulk-jobs' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <BulkJobsList />
          </ProtectedRoute>
        } />
        <Route path='products/bulk-jobs/:jobId/review' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <BulkJobReview />
          </ProtectedRoute>
        } />
        <Route path='discount/add-discount-bulk' element={
          <ProtectedRoute module='discounts' requiredLevel='EDIT'>
            <ConnectedBarcodesPage />
          </ProtectedRoute>
        } />
        <Route path='products/drafted-list' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <DraftProductsListPage />
          </ProtectedRoute>
        } />
        <Route path='products/brand/add' element={
          <ProtectedRoute module='products' requiredLevel='EDIT'>
            <AddBrandPage />
          </ProtectedRoute>
        } />
        <Route path='download-app' element={<DownloadAppPage />} />
        <Route path='feedback' element={<FeedbackPage />} />

        {/* discounts */}
        <Route path='discount/add-using-bulk' element={
          <ProtectedRoute module='discounts' requiredLevel='EDIT'>
            <AddSpecialBulkPage />
          </ProtectedRoute>
        } />
        <Route path='discount/add-selection' element={
          <ProtectedRoute module='discounts' requiredLevel='EDIT'>
            <SpecialSelectionPage />
          </ProtectedRoute>
        } />
        <Route path='discount/pending-special-products' element={
          <ProtectedRoute module='discounts' requiredLevel='EDIT'>
            <PendingSpecialPage />
          </ProtectedRoute>
        } />
        <Route path='discount/remove-discount-bulk' element={
          <ProtectedRoute module='discounts' requiredLevel='EDIT'>
            <RemoveDiscountBulkPage />
          </ProtectedRoute>
        } />
        {/* requests */}
        <Route path='request-suggested-cost' element={
          <ProtectedRoute module='requests' requiredLevel='EDIT'>
            <SuggestedCostReqPage />
          </ProtectedRoute>
        } />
        <Route path='request-add-products' element={
          <ProtectedRoute module='requests' requiredLevel='EDIT'>
            <AddProductReqPage />
          </ProtectedRoute>
        } />
        <Route path='request-update-products' element={
          <ProtectedRoute module='requests' requiredLevel='EDIT'>
            <UpdateProductReqPage />
          </ProtectedRoute>
        } />
        {/*
          Edit button on the Update-Products requests table navigates
          here with the pending pp_edit_product_request id. EditRequestPage
          immediately redirects to /products/product-edit/<id>&edit so
          AddProduct can populate the form from the request payload via
          getRequestDataToEdit(type='edit'). Without this route the
          button used to 404.
        */}
        <Route path='requests/edit/:editRequestId' element={
          <ProtectedRoute module='requests' requiredLevel='EDIT'>
            <EditRequestPage />
          </ProtectedRoute>
        } />
        <Route path='request-add-discount' element={
          <ProtectedRoute module='requests' requiredLevel='EDIT'>
            <AddDiscountReq />
          </ProtectedRoute>
        } />

        {/* daily deal — gated by the tenant's `daily_deals` feature AND user RBAC */}
        <Route path='daily-deal/list' element={
          <TenantFeatureRoute feature='daily_deals'>
            <ProtectedRoute module='daily_deals' requiredLevel='EDIT'>
              <DailyDealListPage />
            </ProtectedRoute>
          </TenantFeatureRoute>
        } />
        <Route path='applied-daily-deal/list' element={
          <TenantFeatureRoute feature='daily_deals'>
            <ProtectedRoute module='daily_deals' requiredLevel='EDIT'>
              <AppliedDailyDealListPage />
            </ProtectedRoute>
          </TenantFeatureRoute>
        } />

        {/* Team Management - Admin only */}
        <Route path='team-management' element={
          <ProtectedRoute module='user_management' requiredLevel='EDIT'>
            <SupplierUsersPage />
          </ProtectedRoute>
        } />
        <Route path='supplier-users' element={
          <ProtectedRoute module='user_management' requiredLevel='EDIT'>
            <SupplierUsersPage />
          </ProtectedRoute>
        } />

        {/* <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='menu-test' element={<MenuTestPage />} /> */}

        {/* Lazy Modules */}
        {/* <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        /> */}
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
